import React, {useState, useEffect, useMemo} from "react"
import {observer} from "mobx-react-lite"
import "./index.scss"
import TableBody from "./TableBody"
import TableHeader from "./TableHeader"
import {UseStores} from "../../hooks/UseStores"
import Pagination from "../Pagination/Pagination"
import PostsPerPage from "../Pagination/PostsPerPage"
import NoResultsIcon from "../../assets/svg/no-search-results-icon.svg"

const SingleTable = ({
  data,
  columns,
  pagination,
  onRowClick,
  selectableRows,
  selectedRow,
  searchKey = "",
}) => {
  const [isHover, setHover] = useState() //  to maintain hover state
  const [hoveredColumnkey, setHoveredColumnkey] = useState() // to maintain specific column on hover
  const [isAsc, setAsc] = useState(true) // for sorting direction
  const [currentSortKey, setCurrentSortKey] = useState(null) // for sorting column

  const [selectedRowId, setSelectedRowId] = useState(selectedRow) // for row selection color change

  const {paginationStore, timePeriodStore, locGroupStore} = UseStores()

  let currentPage = paginationStore.currentPage
  let postPerPage = paginationStore.postPerPage
  let locGeoTime = locGroupStore.locGeoTime
  let timePeriod = timePeriodStore.timePeriod

  const doSort = (arr) => {
    let sorted = arr && arr.slice()
    sorted.sort((a, b) => {
      if (a[currentSortKey] < b[currentSortKey]) {
        return isAsc ? -1 : 1
      }
      if (a[currentSortKey] > b[currentSortKey]) {
        return isAsc ? 1 : -1
      }
      return 0
    })
    return sorted
  }
  const setAscAndKey = (key) => {
    let newAsc = currentSortKey === key ? !isAsc : true
    setAsc(newAsc)
    setCurrentSortKey(key)
  }

  const sortedData = useMemo(() => {
    return currentSortKey ? doSort(data) : data
  }, [data, isAsc, currentSortKey])

  useEffect(() => {
    // To set the active page as the page which contains the selected row
    let tableData = locGroupStore.tableStates.tableData
    if (tableData) {
      if (
        timePeriod === "SEVEN_DAYS" &&
        locGeoTime.geo.geoType === "COUNTRY" &&
        selectedRowId &&
        sortedData
      ) {
        let sortedData
        if (currentSortKey) {
          sortedData = doSort(tableData)
        } else {
          sortedData = tableData
        }
        setPaginationForSelectedRow(selectedRowId, sortedData)
      }
    }
  }, [locGeoTime])

  useEffect(() => {
    if (selectedRow) setSelectedRowId(selectedRow)
    else setSelectedRowId(0)

    if (selectedRow && sortedData) {
      //set the active page to the page containing the selected row
      setPaginationForSelectedRow(selectedRow)
    } else {
      handleRowClick(sortedData[0])
      paginationStore.setCurrentPage(1)
    }

    //scroll to selected row - from search location groups
    setTimeout(() => {
      const elm = document.getElementById(selectedRow)
      if (elm !== null) {
        elm.scrollIntoView({
          behavior: "smooth",
          block: "center",
        })
      }
    }, 250)
  }, [selectedRow])

  useEffect(() => {
    //scroll to selected row on changing number of display rows
    setTimeout(() => {
      const elm = document.getElementById(selectedRow)
      if (elm !== null) {
        elm.scrollIntoView({
          behavior: "smooth",
          block: "center",
        })
      }
    }, 250)
  }, [postPerPage])

  const hoverState = (isHover, key) => {
    // maintain hovered column and state
    setHoveredColumnkey(key)
    setHover(isHover)
  }

  const handleRowClick = (rowData) => {
    // handle row click
    if (selectableRows) {
      setSelectedRowId(rowData.id)
    }
    if (onRowClick) {
      onRowClick(rowData)
    }
  }

  let totalRecords = data.length
  const totalPages = Math.ceil(totalRecords / postPerPage)

  const changePage = (index) => {
    let pageTo = index + 1
    paginationStore.setCurrentPage(pageTo)
  }

  const changePostsPerPage = (postPerPage) => {
    paginationStore.setPostPerPage(postPerPage)
    if (selectedRowId && data) {
      //set the active page to the page containing the selected row
      let selectedRowIndex = findIndexOfSelectedRow(selectedRowId)
      if (selectedRowIndex !== -1) {
        paginationStore.moveToPage(selectedRowIndex)
      } else {
        paginationStore.setCurrentPage(1)
      }
    } else {
      paginationStore.setCurrentPage(1)
    }
  }

  const findIndexOfSelectedRow = (selectedRow, tableData = sortedData) => {
    let arr = tableData.map((row) => row.id)
    let selectedRowIndex = arr.indexOf(selectedRow)
    return selectedRowIndex
  }

  const setPaginationForSelectedRow = (selectedRow, tableData = sortedData) => {
    let selectedRowIndex = findIndexOfSelectedRow(selectedRow, tableData)
    if (selectedRowIndex !== -1) {
      paginationStore.moveToPage(selectedRowIndex)
    } else {
      selectableRows && setSelectedRowId(sortedData[0].id)
      paginationStore.setCurrentPage(1)
    }
  }

  return (
    <div className="table-container" data-testid="table-container">
      <div className="table-content">
        <table tabIndex="0">
          <TableHeader
            columns={columns}
            hoverState={hoverState}
            sortDirection={isAsc}
            sortKey={currentSortKey}
            setAscAndKey={setAscAndKey}
          />
          {sortedData.length > 0 && (
            <TableBody
              data={sortedData}
              columns={columns}
              onRowClick={handleRowClick}
              selectedRowId={selectedRowId}
              hoveredColumnkey={hoveredColumnkey}
              isHover={isHover}
              currentPage={currentPage}
              postPerPage={postPerPage}
              pagination={pagination}
              selectableRows={selectableRows}
            />
          )}
        </table>
      </div>

      {sortedData.length === 0 && (
        <div className="no-results-wrapper">
          <div className="no-results">
            <img src={NoResultsIcon} alt="no results text" className="no-results-icon" />
            <div className="no-results-text">
              {searchKey ? `No results found for "${searchKey}"` : "No results found"}
            </div>
          </div>
        </div>
      )}
      {pagination ? (
        <div className="table-footer" data-testid="table-footer">
          <PostsPerPage
            selected={postPerPage}
            totalRecords={totalRecords}
            perPageHandler={changePostsPerPage}
          />
          <Pagination
            totalPages={totalPages}
            activeIndex={currentPage - 1}
            onPageChange={changePage}
          />
        </div>
      ) : (
        ""
      )}
    </div>
  )
}
export default observer(SingleTable)
